import React, { useEffect, useState } from "react";
import { stageAssignmentsAPI } from "../../api/client";

const formatNoteDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const fullName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || "Someone";

/**
 * Instructions written to the person doing this stage, pinned above the task
 * list they refer to.
 *
 * Deliberately separate from StageNotesSection: that is a conversation thread
 * and sits below the tasks, which is the wrong place for "you take Task C and
 * Task D" — the assignee needs it before they read the checklist, not after.
 *
 * Filtered to notes aimed at THIS assignment, so on a stage with several
 * assignees each person sees only their own share of the work.
 */
export default function MyInstructionsPanel({
  projectId,
  stageId,
  assignmentId,
}) {
  const [instructions, setInstructions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !stageId || !assignmentId) return undefined;
    stageAssignmentsAPI
      .getStageNotes(projectId, stageId)
      .then((resp) => {
        if (cancelled) return;
        setInstructions(
          (resp.data || []).filter(
            (note) =>
              note.direction === "TO_ASSIGNEE" &&
              note.stageAssignmentId === assignmentId,
          ),
        );
      })
      // Instructions are supplementary to the task list; a failure here must
      // not block the assignee from working, so it fails quietly.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, stageId, assignmentId]);

  if (!instructions.length) return null;

  const latest = instructions[instructions.length - 1];
  const earlier = instructions.slice(0, -1);

  return (
    <div className="my-instructions">
      <div className="my-instructions-head">
        <span className="my-instructions-icon" aria-hidden="true">
          📋
        </span>
        <strong>Your instructions for this stage</strong>
      </div>

      <div className="my-instructions-body">{latest.body}</div>
      <div className="my-instructions-meta">
        From {fullName(latest.author)} · {formatNoteDate(latest.createdAt)}
      </div>

      {earlier.length > 0 && (
        <details className="my-instructions-earlier">
          <summary>
            {earlier.length} earlier instruction
            {earlier.length === 1 ? "" : "s"}
          </summary>
          {earlier
            .slice()
            .reverse()
            .map((note) => (
              <div key={note.id} className="my-instructions-earlier-item">
                <div>{note.body}</div>
                <div className="my-instructions-meta">
                  {fullName(note.author)} · {formatNoteDate(note.createdAt)}
                </div>
              </div>
            ))}
        </details>
      )}
    </div>
  );
}
